import React, { useEffect } from 'react'
import {
  StyleProperty,
  StyledContainer,
  StyledContinueButtonContainer,
  StyledPropertyLabel,
  StyledPropertyValue,
  StyledSummaryContainer,
} from '../../ProductCheckout/ProductCheckout.styled'
import reportIcon from 'assets/images/icons/SprengnetterAppsReport.png'
import mapIcon from 'assets/images/icons/SprengnetterAppsMaps1x.png'
import { useTranslation } from 'react-i18next'
import { selectAddressFormData, setAddressData } from 'slices/addressFormSlice'
import { useSelector } from 'react-redux'
import checkMarkYes from 'assets/images/icons/checkMarkYes.svg'
import checkMarkNo from 'assets/images/icons/checkMarkNo.svg'
import Button from 'components/Button/Button'
import { useCreateAddressMutation, useUpdateAddressMutation } from 'services/address-api'
import { sanitizeAddressData } from '../../helper'
import { useAppDispatch } from 'store/hooks'
import { useToastEffect } from 'components/Toast/hooks/useToastEffect'
import { EquipmentMatrixSurfaceMountedInstallation, ObjectType } from 'types/ApiResponse'
import { EquipmentMethod } from 'types/EquipmentMethod'

const MapOrReportSummary = (props: any) => {
  const { setCurrentSubTab, setCurrentTab } = props
  const { t } = useTranslation()
  const addressFormData = useSelector(selectAddressFormData)
  const dispatch = useAppDispatch()
  const isReport = addressFormData.productName === 'report'
  const objectType = addressFormData.addressData?.propertyDetails?.objectType
  const equipmentMatrix = addressFormData.addressData?.propertyDetails?.equipmentMatrix

  const [
    createAddress,
    {
      isLoading: isCrateAddressLoading,
      isSuccess: isCrateAddressSuccess,
      isError: isCrateAddressError,
      data: createAddressData,
    },
  ] = useCreateAddressMutation()

  const [
    updateAddress,
    {
      isLoading: isUpdateAddressLoading,
      isSuccess: isUpdateAddressSuccess,
      isError: isUpdateAddressError,
      data: updateAddressData,
    },
  ] = useUpdateAddressMutation()

  const isLoading = isUpdateAddressLoading || isCrateAddressLoading

  const getBooleanValue = (value?: boolean) => {
    return (
      <>
        {`${value}` === `true` ? (
          <img src={checkMarkYes} alt="checkMarkYes" />
        ) : `${value}` === `false` ? (
          <img src={checkMarkNo} alt="checkMarkNo" />
        ) : null}
        {`${value}` === `true` ? t('common.yes') : `${value}` === `false` ? t('common.no') : '—'}
      </>
    )
  }

  const onBackClick = () => {
    setCurrentTab((tab) => tab - 1)
  }

  const onContinueClick = () => {
    const data = sanitizeAddressData(addressFormData.addressData)
    if (addressFormData.isEdit) {
      updateAddress({
        id: addressFormData.addressData.id,
        payload: data,
      })
    } else {
      createAddress(data)
    }
  }

  useEffect(() => {
    if (isCrateAddressSuccess) {
      dispatch(setAddressData(createAddressData))
      setCurrentSubTab((tab) => tab + 1)
    }
  }, [isCrateAddressSuccess, createAddressData, setCurrentSubTab, dispatch])

  useEffect(() => {
    if (isUpdateAddressSuccess) {
      dispatch(setAddressData(updateAddressData))
      setCurrentSubTab((tab) => tab + 1)
    }
  }, [isUpdateAddressSuccess, updateAddressData, setCurrentSubTab, dispatch])

  useToastEffect(t('newAddressModal.addressTab.create-address.error.toast'), 'ERROR', [
    isCrateAddressError || isUpdateAddressError,
  ])

  return (
    <>
      <StyledContainer>
        <img src={isReport ? reportIcon : mapIcon} alt="" className="app-icon" />
        <h1>{isReport ? t('common.generateReport') : t('common.generateMap')}</h1>
        <h2>{t('checkoutTab.summary')}</h2>
        <StyledSummaryContainer>
          <StyleProperty>
            <StyledPropertyLabel>{t('leadsList.addressFilter')}</StyledPropertyLabel>
            <StyledPropertyValue>
              {`${addressFormData.addressData.street} ${addressFormData.addressData.houseNumber}`}
              <br />
              {`${addressFormData.addressData.town}, ${addressFormData.addressData.zip}`}
              <br />
              {`${addressFormData.addressData.nation}`}
            </StyledPropertyValue>
          </StyleProperty>
          {/* <StyleProperty>
            <StyledPropertyLabel>{t('newAddressModal.valuationDate')}</StyledPropertyLabel>
            <StyledPropertyValue>{addressFormData.addressData?.valuationDate}</StyledPropertyValue>
          </StyleProperty> */}
          <StyleProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.objectType')}
            </StyledPropertyLabel>
            <StyledPropertyValue>{t(`objectType.${objectType.toLowerCase()}`)}</StyledPropertyValue>
          </StyleProperty>
          {objectType !== ObjectType.MULTI_FAMILY_HOUSE && (
            <StyleProperty>
              <StyledPropertyLabel>
                {t('newAddressModal.objectDataTab.houseType')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {t(
                  `houseOrApartmentType.${addressFormData.addressData.propertyDetails?.houseOrApartmentType.toLowerCase()}`
                )}
              </StyledPropertyValue>
            </StyleProperty>
          )}
          <StyleProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.buildYear')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {addressFormData.addressData?.propertyDetails?.constructionYear}
            </StyledPropertyValue>
          </StyleProperty>
          <StyleProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.livingArea')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {`${addressFormData.addressData?.propertyDetails?.livingArea} m²`}
            </StyledPropertyValue>
          </StyleProperty>
          {objectType === ObjectType.APARTMENT ? null : (
            <StyleProperty>
              <StyledPropertyLabel>
                {t('newAddressModal.objectDataTab.plotArea')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {`${addressFormData.addressData?.propertyDetails?.plotArea} m²`}
              </StyledPropertyValue>
            </StyleProperty>
          )}
          {objectType !== ObjectType.APARTMENT && (
            <StyleProperty>
              <StyledPropertyLabel>
                {t('newAddressModal.objectDataTab.numberOfFloors')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {addressFormData.addressData?.propertyDetails?.numberOfFloors
                  ? addressFormData.addressData?.propertyDetails?.numberOfFloors
                  : '—'}
              </StyledPropertyValue>
            </StyleProperty>
          )}
          {objectType === ObjectType.APARTMENT && (
            <StyleProperty>
              <StyledPropertyLabel>{t('newAddressModal.objectDataTab.floor')}</StyledPropertyLabel>
              <StyledPropertyValue>
                {addressFormData.addressData?.propertyDetails?.floorNumber
                  ? addressFormData.addressData?.propertyDetails?.floorNumber
                  : '—'}
              </StyledPropertyValue>
            </StyleProperty>
          )}
          {objectType === ObjectType.APARTMENT ? (
            <StyleProperty>
              <StyledPropertyLabel>
                {t('newAddressModal.objectDataTab.elevator')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {getBooleanValue(addressFormData.addressData?.propertyDetails?.elevator)}
              </StyledPropertyValue>
            </StyleProperty>
          ) : null}
          <StyleProperty>
            <StyledPropertyLabel>{t('newAddressModal.objectDataTab.garage')}</StyledPropertyLabel>
            <StyledPropertyValue>
              {addressFormData.addressData?.propertyDetails?.garages
                ? addressFormData.addressData?.propertyDetails?.garages
                : '—'}
            </StyledPropertyValue>
          </StyleProperty>
          <StyleProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.parkingPlace')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {addressFormData.addressData?.propertyDetails?.outdoorParkingSpaces
                ? addressFormData.addressData?.propertyDetails?.outdoorParkingSpaces
                : '—'}
            </StyledPropertyValue>
          </StyleProperty>
          <StyleProperty>
            <StyledPropertyLabel>{t('addressModal.equipmentMethod')}</StyledPropertyLabel>
            <StyledPropertyValue>
              {t(
                `newAddressModal.furnishingTab.equipmentMethod.${addressFormData.addressData?.propertyDetails?.equipmentMethod?.toLowerCase()}`
              )}
            </StyledPropertyValue>
          </StyleProperty>
          {addressFormData.addressData?.propertyDetails?.equipmentMethod ===
            EquipmentMethod.SIMPLE && (
            <StyleProperty>
              <StyledPropertyLabel>{t('addressModal.equipmentLevel')}</StyledPropertyLabel>
              <StyledPropertyValue>
                {t(
                  `equipmentValue.${addressFormData.addressData?.propertyDetails?.equipmentValue?.toLowerCase()}`
                )}
              </StyledPropertyValue>
            </StyleProperty>
          )}
          {addressFormData.addressData?.propertyDetails?.equipmentMethod ===
            EquipmentMethod.DETAILED && (
            <>
              <StyleProperty>
                <StyledPropertyLabel>
                  {t('newAddressModal.furnishingTab.heatingOptions.label')}
                </StyledPropertyLabel>
                <StyledPropertyValue>
                  {equipmentMatrix?.heating
                    ? t(`equipmentMatrix.heating.${equipmentMatrix?.heating?.toLowerCase()}`)
                    : '—'}
                </StyledPropertyValue>
              </StyleProperty>
              {objectType !== ObjectType.APARTMENT && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.furnishingTab.roofOptions.label')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {equipmentMatrix?.roofCovering
                      ? t(
                          `equipmentMatrix.roofCovering.${equipmentMatrix?.roofCovering?.toLowerCase()}`
                        )
                      : '—'}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              <StyleProperty>
                <StyledPropertyLabel>
                  {t('newAddressModal.furnishingTab.windowsOptions.label')}
                </StyledPropertyLabel>
                <StyledPropertyValue>
                  {equipmentMatrix?.windows
                    ? t(`equipmentMatrix.windows.${equipmentMatrix?.windows?.toLowerCase()}`)
                    : '—'}
                </StyledPropertyValue>
              </StyleProperty>
              {(objectType === ObjectType.APARTMENT ||
                objectType === ObjectType.MULTI_FAMILY_HOUSE) && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.furnishingTab.storageOptions.label')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {equipmentMatrix?.storeRoom
                      ? t(`equipmentMatrix.storeRoom.${equipmentMatrix?.storeRoom?.toLowerCase()}`)
                      : '—'}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType === ObjectType.APARTMENT && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.furnishingTab.floorCoveringOptions.label')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {equipmentMatrix?.floorCovering
                      ? t(
                          `equipmentMatrix.floorCovering.${equipmentMatrix?.floorCovering?.toLowerCase()}`
                        )
                      : '—'}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType !== ObjectType.HOUSE && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.furnishingTab.outdoorLivingAreaOptions.label')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {equipmentMatrix?.residentialArea
                      ? t(
                          `equipmentMatrix.residentialArea.${equipmentMatrix?.residentialArea?.toLowerCase()}`
                        )
                      : '—'}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType !== ObjectType.MULTI_FAMILY_HOUSE && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.furnishingTab.bathOptions.label')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {equipmentMatrix?.bathroom
                      ? t(`equipmentMatrix.bathroom.${equipmentMatrix?.bathroom?.toLowerCase()}`)
                      : '—'}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              <StyleProperty>
                <StyledPropertyLabel>
                  {t('newAddressModal.furnishingTab.separateGuestToilet')}
                </StyledPropertyLabel>
                <StyledPropertyValue>
                  {getBooleanValue(equipmentMatrix?.guestToilet)}
                </StyledPropertyValue>
              </StyleProperty>
              <StyleProperty>
                <StyledPropertyLabel>
                  {t('newAddressModal.furnishingTab.insulatedOuterWalls')}
                </StyledPropertyLabel>
                <StyledPropertyValue>
                  {getBooleanValue(equipmentMatrix?.insulatedExteriorWalls)}
                </StyledPropertyValue>
              </StyleProperty>
              <StyleProperty>
                <StyledPropertyLabel>
                  {t('newAddressModal.furnishingTab.linesOnPlaster')}
                </StyledPropertyLabel>
                <StyledPropertyValue>
                  {getBooleanValue(
                    equipmentMatrix?.surfaceMountedInstallation ===
                      EquipmentMatrixSurfaceMountedInstallation.CABLES_ON_WALLS
                  )}
                </StyledPropertyValue>
              </StyleProperty>
            </>
          )}
        </StyledSummaryContainer>
      </StyledContainer>
      <StyledContinueButtonContainer>
        <Button kind="primary" onClick={() => onBackClick()}>
          {t('common.backButton')}
        </Button>
        <Button
          kind="primary"
          loading={isLoading}
          disabled={isLoading}
          onClick={() => onContinueClick()}
        >
          {isLoading ? t('common.loadingButton') : t('common.saveAndProceedButton')}
        </Button>
      </StyledContinueButtonContainer>
    </>
  )
}

export default MapOrReportSummary
